import { and, not } from "xstate";

import { DigitalVisitReasonEnum } from "@/lib/__generated/api";
import { DataLayerStepNames } from "@/lib/constants";
import { getBaseDataLayerParams } from "@/lib/utils/dataLayerParams";

import { schedulerSetup } from "../schedulerSetup";

export const patientInformationState = schedulerSetup.createStateConfig({
  meta: {
    componentKey: "patient-information",
    label: "patientInformation",
    showFooter: true,
    getSubtitle: (ctx) =>
      ctx.patientData.firstName
        ? `${ctx.patientData.firstName} ${ctx.patientData.lastName}`
        : undefined,
  },
  initial: "checkingFlow",
  entry: [
    {
      type: "dataLayerPush",
      params: ({ context }) => ({
        step_name: DataLayerStepNames.patientInformation,
        ...getBaseDataLayerParams(context),
      }),
    },
    {
      type: "dataLayerPush",
      params: ({ context }) => ({
        step_name: DataLayerStepNames.contactInformation,
        ...getBaseDataLayerParams(context),
      }),
    },
  ],
  states: {
    checkingFlow: {
      always: [
        // Motto and Healthy Mouth Movement skip the needs-based questionnaire; dentures includes it.
        { guard: "requiresNeedsBasedQuestions", target: "loadingQuestions" },
        { target: "idle" },
      ],
    },
    loadingQuestions: {
      tags: ["loading"],
      invoke: {
        src: "fetchQuestions",
        input: ({ context }) => ({
          reasonForVisit: context.reasonForVisit as DigitalVisitReasonEnum,
          language: context.currentLanguage,
        }),
        onDone: {
          target: "idle",
          actions: {
            type: "assignQuestionsData",
            params: ({ event }) => ({ questions: event.output }),
          },
        },
        onError: {
          target: "idle",
          actions: {
            type: "assignError",
            params: ({ event }) => ({
              key: "questionnaire",
              message:
                event.error instanceof Error ? event.error.message : "Failed to load questions.",
            }),
          },
        },
      },
    },
    idle: {
      on: {
        SUBMIT_NEED_BASED_ANSWERS: {
          target: "idle",
          actions: [
            {
              type: "assignNeedBasedQuestions",
              params: ({ event }) => ({ data: event.data }),
            },
            {
              type: "assignNeedBasedQuestionsValid",
              params: ({ event }) => ({
                isValid: event.isValid,
              }),
            },
          ],
        },
        // Branches are checked in order, so each one can assume the ones above it didn't match.
        SUBMIT_COMBINED_PATIENT_CONTACT: [
          {
            // form fields invalid -> show field errors, stay
            guard: not("isPatientFormValid"),
            actions: [
              { type: "markPatientSubmitAttempted" },
              { type: "capturePatientInformationValidation" },
              {
                type: "assignCombinedPatientContactErrors",
                params: ({ event }) => ({
                  data: event.data,
                }),
              },
            ],
          },
          {
            // questions not answered -> stay, component shows question errors
            guard: not("areQuestionsComplete"),
            actions: [
              { type: "markPatientSubmitAttempted" },
              { type: "clearErrors" },
              {
                type: "capturePatientInformationValidation",
                params: { errorType: "nbq_incomplete" },
              },
              {
                type: "assignPatientData",
                params: ({ event }) => ({
                  data: event.data,
                }),
              },
            ],
          },
          {
            // age is ineligible for the reason -> show error dialog, stay
            guard: "isAgeIneligibleForReason",
            actions: [
              { type: "assignAgeIneligibleError" },
              {
                type: "capturePatientInformationValidation",
                params: { errorType: "age_ineligible" },
              },
              {
                type: "assignPatientData",
                params: ({ event }) => ({
                  data: event.data,
                }),
              },
            ],
          },
          {
            // everything valid -> advance
            target: "complete",
            actions: [
              { type: "clearErrors" },
              {
                type: "assignPatientData",
                params: ({ event }) => ({
                  data: event.data,
                }),
              },
              { type: "markStepComplete", params: { step: "patientInformation" } },
              {
                type: "trackFormEvent",
                params: ({ event, context }) => ({
                  firstName: event.data.firstName,
                  lastName: event.data.lastName,
                  emailAddress: event.data.emailAddress ?? "",
                  dateOfBirth: event.data.dateOfBirth,
                  reasonForVisit: context.reasonForVisit ?? "",
                  phoneNumber: event.data.phoneNumber ?? "",
                  smsMarketing: event.data.smsMarketingConsent,
                  smsTransactional: event.data.smsTransactionalConsent,
                }),
              },
            ],
          },
        ],
      },
    },
    complete: { type: "final" },
  },
  on: {
    // Slot-first flows reach this step from dateTimeSelection. In the standard flow it is the first
    // step, so there is nowhere to go back to.
    BACK: { guard: "isSlotFirstFlow", target: "dateTimeSelection" },
    SET_LANGUAGE: {
      target: ".checkingFlow",
      actions: [
        { type: "clearErrors" },
        {
          type: "assignLanguage",
          params: ({ event }) => ({ language: event.language }),
        },
      ],
    },
  },
  onDone: [
    // Slot-first flows already picked the slot, so they skip ahead.
    { guard: and(["isSlotFirstFlow", "isPatientUnder18"]), target: "guarantorInformation" },
    { guard: "isSlotFirstFlow", target: "appointmentReview" },
    { target: "dateTimeSelection" },
  ],
} as const);
